/**
 * What a COMPLETE appointment record is, and — much more importantly — what a
 * TIME is, in one place.
 *
 * ## The datetime contract, and why it is a refusal rather than a coercion
 *
 * The calendar stores an IANA zone on every record (`normalizeEvent`'s `tz`)
 * and **nothing in the app reads it**: `calendarDates.js` and
 * `calendarRecurrence.js` never mention it, and every calculation is local wall
 * clock through the native `Date`. Two consequences, both silent:
 *
 * 1. `instanceOn` re-applies the master's wall clock in the zone of whatever
 *    machine is EXPANDING the series (`calendarRecurrence.js`'s
 *    `dayAtMinutes(day, minutesOfDay(anchor))`), so a series is anchored to the
 *    viewer's clock, not the author's.
 * 2. A naive ISO date-time (`2026-09-15T15:00:00`, no `Z` and no offset) is
 *    parsed by JS as **host**-local. For an agent running in another timezone
 *    that is right by accident and wrong the moment it believed the string
 *    meant its own zone.
 *
 * An MCP client is, by construction, a different process and frequently a
 * different machine. So this module does not guess:
 *
 * - every result names the host zone once, as `timezone`;
 * - every time is returned TWICE — `start`/`end` as absolute UTC instants and
 *   `startLocal`/`endLocal` as the user's wall clock in a form that cannot be
 *   mistaken for an instant (no `Z`, no offset);
 * - a write takes EXACTLY ONE time form: an offset-qualified `start`/`end`, an
 *   explicitly-local `start_local`/`end_local`, or an all-day
 *   `start_date`/`end_date`. **A naive `start` is `invalid_argument`.** Zero
 *   forms on a create is a refusal too; on an update it means "leave the times
 *   alone".
 *
 * `tz` itself is NEVER accepted from a client: `normalizeEvent` stamps the
 * host's zone, which is the user's, and the agent's zone is not a property of
 * the user's appointment.
 *
 * ## Three boundary conventions in one record — do not "align" them
 *
 * - timed `end` is **EXCLUSIVE** (`calendarStore.js`, and `eventRange` uses it
 *   as such);
 * - all-day `end_date` is **INCLUSIVE** (`calendarDates.eventRange` adds +1 day
 *   to get an exclusive end — the Outlook editing convention);
 * - `recurrence.until` is **INCLUSIVE** (`occurrenceDays`'s `day > untilDay`
 *   stop).
 *
 * ## normalizeEvent is delegated to, never re-typed
 *
 * `extension_records_upsert` replaces `data` wholesale, and `normalizeEvent`'s
 * own header says a field left out of its explicit build "silently does not
 * survive a save/sync/reload round-trip". It is the app's own decomposition and
 * the only one the running app agrees with, so every builder here does a
 * read-modify-write through it rather than assembling a record from arguments.
 * (This is `boardRecords.js` → `decomposeBoard`, for the same reason.)
 *
 * ## No sanitizer on this path
 *
 * `description` is PLAIN TEXT: `EventPopover` renders it as
 * `<span style={{whiteSpace:'pre-wrap'}}>{event.description}</span>`,
 * React-escaped, and there is no `dangerouslySetInnerHTML` anywhere under
 * `src/components/calendar/`. Boards needed `sanitizeRichTextHtml` because a
 * card description is HTML; running it here would mangle the user's own
 * newlines for no security gain.
 */
import { invoke } from '@tauri-apps/api/core';
import {
  EXTENSION_ID as CALENDAR_EXTENSION_ID,
  normalizeEvent,
  recordsContext,
  uid as newEventUid,
  MAX_REMINDERS,
} from '../extensions/bundled/calendar/calendarStore';
import { recurrenceProblem } from '../utils/calendarRecurrence';
import { fromDateKey, toDateKey } from '../utils/calendarDates';
import { isOverSyncHint } from './boardRecords';
import { mcpError } from './errors';

export { CALENDAR_EXTENSION_ID, newEventUid, isOverSyncHint };
export const CALENDAR_RECORD_TYPE = 'event';

/** The widest window `appointments_list` will expand (O25). */
export const MAX_WINDOW_DAYS = 370;
/** Cap on `reminders[]`, mirrored in the published input schema (defined in
 *  calendarStore — the read path clamps with it too). */
export { MAX_REMINDERS };

export const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;
/** An instant: it MUST end in `Z` or an explicit ±HH:MM offset. */
export const QUALIFIED_INSTANT = /(Z|[+-]\d{2}:\d{2})$/;
/** An explicitly-local wall clock: no zone designator, ever. */
export const NAIVE_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

/** The user's zone — the same source `normalizeEvent` stamps into `tz`. */
export function hostTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const pad = (n) => String(n).padStart(2, '0');

/**
 * An instant → the user's wall clock as `YYYY-MM-DDTHH:MM:SS`.
 *
 * Deliberately NOT `toISOString().slice(0,19)` — that is UTC wearing a local
 * shape, which is precisely the confusion this whole module exists to remove.
 */
export function toLocalWallClock(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/**
 * A naive `YYYY-MM-DDTHH:MM[:SS]` interpreted as the USER's wall clock → an
 * absolute instant. This is `EventEditorModal.composeLocal`'s arithmetic: build
 * the Date from local components, never `new Date(str)`.
 */
export function fromLocalWallClock(text) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(text));
  if (!m) return null;
  const d = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    Number(m[6] || 0),
    0
  );
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A date key that is a REAL date — a regex accepts `2026-02-31`. */
export function checkDateKey(value, field) {
  if (!DATE_KEY.test(String(value ?? ''))) {
    return mcpError('invalid_argument', `${field} must be a calendar date, as YYYY-MM-DD.`, {
      field,
    });
  }
  const parsed = fromDateKey(String(value));
  if (!parsed || toDateKey(parsed) !== String(value)) {
    return mcpError('invalid_argument', `${field} is not a real date.`, { field });
  }
  return null;
}

/**
 * THE time-form resolver. Returns `{ error }`, `{ times: null }` (no form
 * supplied) or `{ times: {...} }` — the four record fields plus `all_day`.
 *
 * Enforced HERE and not in the published JSON Schema because
 * `dispatch::validate_against_schema` implements neither `format` nor any
 * cross-field rule; phase 2 recorded that gap and this is the first contract
 * that depends on it.
 */
export function resolveTimeForm(args) {
  const hasAbsolute = args.start !== undefined || args.end !== undefined;
  const hasLocal = args.start_local !== undefined || args.end_local !== undefined;
  const hasAllDay =
    args.all_day === true || args.start_date !== undefined || args.end_date !== undefined;

  const forms = [hasAbsolute, hasLocal, hasAllDay].filter(Boolean).length;
  if (forms === 0) return { times: null };
  if (forms > 1) {
    return {
      error: mcpError(
        'invalid_argument',
        'Send exactly one time form: start/end (with Z or an offset), start_local/end_local (the user\'s wall clock), or all_day with start_date/end_date.',
        { field: 'start' }
      ),
    };
  }

  if (hasAllDay) {
    if (args.all_day !== true) {
      return {
        error: mcpError(
          'invalid_argument',
          'start_date and end_date describe an all-day appointment, so all_day must be true.',
          { field: 'all_day' }
        ),
      };
    }
    if (args.start_date === undefined) {
      return {
        error: mcpError('invalid_argument', 'all_day needs a start_date (YYYY-MM-DD).', {
          field: 'start_date',
        }),
      };
    }
    const badStart = checkDateKey(args.start_date, 'start_date');
    if (badStart) return { error: badStart };
    const endDate = args.end_date === undefined || args.end_date === null ? args.start_date : args.end_date;
    const badEnd = checkDateKey(endDate, 'end_date');
    if (badEnd) return { error: badEnd };
    if (String(endDate) < String(args.start_date)) {
      return {
        error: mcpError(
          'invalid_argument',
          'end_date is before start_date. end_date is INCLUSIVE — a one-day appointment repeats start_date.',
          { field: 'end_date' }
        ),
      };
    }
    return {
      times: {
        all_day: true,
        start: null,
        end: null,
        start_date: String(args.start_date),
        end_date: String(endDate),
      },
    };
  }

  if (hasLocal) {
    if (args.start_local === undefined) {
      return {
        error: mcpError('invalid_argument', 'start_local is required with end_local.', {
          field: 'start_local',
        }),
      };
    }
    for (const field of ['start_local', 'end_local']) {
      const raw = args[field];
      if (raw === undefined) continue;
      const text = String(raw);
      if (QUALIFIED_INSTANT.test(text)) {
        return {
          error: mcpError(
            'invalid_argument',
            `${field} carries a timezone. ${field} means the USER's local wall clock — send an absolute instant as start/end instead.`,
            { field }
          ),
        };
      }
      if (!NAIVE_LOCAL.test(text) || !fromLocalWallClock(text)) {
        return {
          error: mcpError(
            'invalid_argument',
            `${field} must be YYYY-MM-DDTHH:MM or YYYY-MM-DDTHH:MM:SS, with no timezone — it means the user's local wall clock.`,
            { field }
          ),
        };
      }
    }
    const start = fromLocalWallClock(String(args.start_local));
    const end =
      args.end_local === undefined || args.end_local === null
        ? new Date(start.getTime() + 30 * 60000)
        : fromLocalWallClock(String(args.end_local));
    if (end < start) {
      return {
        error: mcpError('invalid_argument', 'end_local is before start_local.', {
          field: 'end_local',
        }),
      };
    }
    return {
      times: {
        all_day: false,
        start: start.toISOString(),
        end: end.toISOString(),
        start_date: null,
        end_date: null,
      },
    };
  }

  // Absolute. This is the arm the whole contract exists for.
  if (args.start === undefined) {
    return {
      error: mcpError('invalid_argument', 'start is required with end.', { field: 'start' }),
    };
  }
  for (const field of ['start', 'end']) {
    const raw = args[field];
    if (raw === undefined) continue;
    const text = String(raw);
    if (!QUALIFIED_INSTANT.test(text)) {
      return {
        error: mcpError(
          'invalid_argument',
          `${field} has no timezone, so it cannot be placed on the user's calendar — the server will NOT guess. ` +
            `Send it with Z or an explicit ±HH:MM offset, or send start_local/end_local, which mean the user's own wall clock. ` +
            `The user's timezone is reported as \`timezone\` on every calendar result.`,
          { field }
        ),
      };
    }
    if (Number.isNaN(new Date(text).getTime())) {
      return {
        error: mcpError('invalid_argument', `${field} is not a valid date-time.`, { field }),
      };
    }
  }
  const start = new Date(String(args.start));
  const end =
    args.end === undefined || args.end === null
      ? new Date(start.getTime() + 30 * 60000)
      : new Date(String(args.end));
  if (end < start) {
    return { error: mcpError('invalid_argument', 'end is before start.', { field: 'end' }) };
  }
  return {
    times: {
      all_day: false,
      start: start.toISOString(),
      end: end.toISOString(),
      start_date: null,
      end_date: null,
    },
  };
}

/**
 * `reminders` is an array of MINUTES BEFORE on the wire; the record's
 * `[{minutes_before}]` shape is an internal detail nobody outside should have
 * to know. Returns `{ error }` or `{ reminders: [{minutes_before}] }`.
 */
export function resolveReminders(value) {
  if (value === undefined) return { reminders: undefined };
  if (!Array.isArray(value)) {
    return {
      error: mcpError('invalid_argument', 'reminders must be an array of whole minutes before.', {
        field: 'reminders',
      }),
    };
  }
  if (value.length > MAX_REMINDERS) {
    return {
      error: mcpError('invalid_argument', `At most ${MAX_REMINDERS} reminders.`, {
        field: 'reminders',
      }),
    };
  }
  for (const entry of value) {
    if (!Number.isInteger(entry) || entry < 0) {
      return {
        error: mcpError(
          'invalid_argument',
          'Each reminder is a whole number of minutes before the appointment, 0 or greater.',
          { field: 'reminders' }
        ),
      };
    }
  }
  return { reminders: value.map((m) => ({ minutes_before: m })) };
}

/**
 * The recurrence rule, validated by the app's OWN validator. One validator,
 * and its reason string reaches the model verbatim — a second copy here would
 * be a second opinion about what the calendar can express.
 */
export function checkRecurrence(rule) {
  if (rule === undefined || rule === null) return null;
  if (typeof rule !== 'object' || Array.isArray(rule)) {
    return mcpError('invalid_argument', 'recurrence must be an object or null.', {
      field: 'recurrence',
    });
  }
  const problem = recurrenceProblem(rule);
  if (problem) {
    return mcpError('invalid_argument', `recurrence is not usable: ${problem}.`, {
      field: 'recurrence',
    });
  }
  return null;
}

/** Every event record of one calendar scope, flat, exactly as Rust returns it. */
export async function listCalendarRows(scopeKey) {
  const rows = await invoke('extension_records_list', {
    workspaceId: scopeKey,
    extensionId: CALENDAR_EXTENSION_ID,
    recordType: CALENDAR_RECORD_TYPE,
    scopeId: null,
  });
  return rows || [];
}

/**
 * `id -> rev` for one calendar scope.
 *
 * `scopeId: null` because the calendar has no `data.scope_id` and
 * `extension_records_list` is called the same way — the whole workspace bucket
 * IS the calendar. That is why this phase needs no `sync/records.rs` change.
 *
 * Best-effort in the same VISIBLE way `board_read`'s is: a failed metadata read
 * leaves every rev null, and a null rev makes `appointment_update` refuse with
 * `expected_rev_required` rather than write against an unknown base.
 */
export async function revsForCalendar(scopeKey) {
  try {
    const rows = await invoke('extension_records_list_meta', {
      workspaceId: scopeKey,
      extensionId: CALENDAR_EXTENSION_ID,
      recordType: CALENDAR_RECORD_TYPE,
      scopeId: null,
    });
    return new Map((rows || []).map((r) => [r.id, r.rev ?? null]));
  } catch {
    return new Map();
  }
}

/** Flat row → the event `data`, i.e. minus the four envelope columns. */
export function eventOf(row) {
  if (!row) return null;
  const { id, record_type: recordType, parent_id: parentId, sort_key: sortKey, ...data } = row;
  return { ...data, uid: data.uid || id };
}

/** The upsert record for one complete event, exactly as `saveEvent` builds it. */
export function recordForEvent(event) {
  return {
    id: event.uid,
    record_type: CALENDAR_RECORD_TYPE,
    // Mirrors the master for DB hygiene only — `data.override_of` is
    // authoritative and `rowToEvent` drops this (`calendarStore.saveEvent`).
    parent_id: event.override_of ? event.override_of.master_uid : null,
    sort_key: null,
    data: event,
  };
}

/** The scope key an event's own `calendar` field routes to. */
export function scopeKeyForCalendar(calendarKey) {
  return recordsContext(calendarKey).workspaceId;
}

/**
 * A read-modify-write through the app's own `normalizeEvent`.
 *
 * `existing` is the row's current `data`; `patch` carries only what the caller
 * asked to change. Anything not in `patch` survives because it is spread first
 * — including `origin`, `created_by`, `color`, `exdates` and any field the app
 * gains before this file learns about it.
 *
 * `uid`, `tz`, `created_at` and `calendar` are pinned from the existing record:
 * a client may not re-home, re-identify or re-zone an appointment.
 */
export function patchedEvent(existing, patch) {
  return normalizeEvent({
    ...existing,
    ...patch,
    uid: existing.uid,
    tz: existing.tz,
    calendar: existing.calendar,
    created_at: existing.created_at,
  });
}

/**
 * The row projection — EXPLICIT, field by field.
 *
 * `board_read` returns each task as `{...t}`, and its own reviewer flagged that
 * every field a record ever gains is exposed automatically. Not repeated here:
 * `origin`, `created_by`, `color`, `tz` and `override_of` are deliberately
 * absent (`exdates` and `override_of` appear in `appointment_read` only, where
 * they are the point).
 *
 * `instance` is what `expandEvents` produced — a generated occurrence, a
 * materialized override record, or a plain single event. The caller resolves
 * the identity (`appointmentId`, `occurrenceDate`) and the CAS target (`rev`,
 * `record`) because those differ per kind and getting them from the row alone
 * is exactly the bug §B3 exists to prevent. `record` is the record the `rev`
 * names, so `syncable` measures the bytes that would actually be written.
 */
export function projectOccurrence(
  instance,
  { scope, rev, overrideRecordId = null, appointmentId, occurrenceDate, recurring, recurrence, record }
) {
  const source = record || instance;
  return {
    // Always the MASTER's uid — for a generated instance, for a materialized
    // override, and for a single event, which is its own master. ONE identity,
    // and it is the one every write tool takes. `overrideRecordId` is returned
    // for transparency and is NOT accepted as an argument.
    appointmentId,
    occurrenceDate: occurrenceDate ?? null,
    recurring: !!recurring,
    overrideRecordId,
    title: instance.title || '',
    location: instance.location || '',
    description: instance.description || '',
    allDay: instance.all_day === true,
    start: instance.all_day ? null : instance.start || null,
    end: instance.all_day ? null : instance.end || null,
    startLocal: instance.all_day ? null : toLocalWallClock(instance.start),
    endLocal: instance.all_day ? null : toLocalWallClock(instance.end),
    startDate: instance.all_day ? instance.start_date || null : null,
    endDate: instance.all_day ? instance.end_date || null : null,
    status: instance.status === 'cancelled' ? 'cancelled' : 'confirmed',
    reminders: (Array.isArray(instance.reminders) ? instance.reminders : []).map(
      (r) => r.minutes_before
    ),
    recurrence: recurrence ?? null,
    scope,
    rev: rev ?? null,
    // Diagnostic, never enforcement — the real refusal is `cas_upsert_in`'s.
    syncable: !isOverSyncHint(source),
  };
}
